import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader2, UserCog, LogOut, Repeat } from "lucide-react";

import { instance } from "../axios/axios";
import {
  silentRefetchUserInfo,
  stopImpersonation,
} from "../features/auth/authSlice";
import { setContentState } from "../features/Content/contentTextSlice";
import { useToast } from "../hooks/use-toast";
import { Button } from "./ui/button";

// Persistent banner shown above every route while impersonating. Single-tap
// swap-back → POST /user/portal/auth/impersonate/stop → restore viewer token.
const ImpersonationBanner = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const isImpersonating = useSelector((s) => s.auth.isImpersonating);
  const target = useSelector((s) => s.auth.impersonationTarget);
  const token = useSelector((s) => s.auth.token);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // WHEN AN IMPERSONATION ENDS, THE VIEWER GOES TO /home. That is decision (A)
  // of "Flow: Stopping an impersonation" in
  // tech_map_2.0/systems/portal-ui/intent-ui-patterns.md, confirmed by Levi in
  // this thread (msg 1a1455f75ded3ee9, "go"). Ending an impersonation used to
  // change who you are without changing where you are: the URL stayed on the
  // page you had opened as the other person, and the next render painted it as
  // you — which is how Levi ended up on a permission-refusal page with no way
  // back to the portal (thread #1612 r1).
  //
  // This effect is deliberately the ONLY place that navigates, because BOTH
  // end paths flip the same flag: the Swap back button below dispatches
  // `stopImpersonation()`, and `authSlice`'s REHYDRATE handler strips a
  // persisted impersonation on load. Watching the transition covers the PWA
  // case too, where there is no button to press. The banner stays MOUNTED on
  // every route — `return null` below hides it, it does not unmount it — so
  // the effect keeps running while the bar is invisible.
  //
  // `replace` on purpose: the carried-over page belongs to the other person's
  // session, so it must not be one browser-Back press away.
  const wasImpersonating = useRef(isImpersonating);
  useEffect(() => {
    if (wasImpersonating.current && !isImpersonating) {
      dispatch(setContentState(""));
      navigate("/home", { replace: true });
    }
    wasImpersonating.current = isImpersonating;
  }, [isImpersonating, dispatch, navigate]);

  if (!isImpersonating) return null;

  // Open the Quick Log picker without swapping back first. The picker itself
  // (QuickLogContent) detects isImpersonating and performs a direct switch
  // using the preserved viewer token, so the next pick moves straight to the
  // new portal — no Swap back → re-open Quick Log round-trip.
  const onQuickLog = () => dispatch(setContentState("Quick Log"));

  const onSwapBack = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Best-effort POST /stop — restore viewer locally even if it fails.
      await instance
        .post(
          "/user/portal/auth/impersonate/stop",
          {},
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        )
        .catch(() => null);
      dispatch(stopImpersonation());
      await dispatch(silentRefetchUserInfo()).unwrap().catch(() => null);
      // No navigation and no content reset here on purpose: ending the
      // impersonation is what returns the viewer to /home with an empty
      // content key, and the effect above owns that so the REHYDRATE path
      // (app resumed as someone, no button to press) gets it too.
      toast({
        title: "Back in your portal",
        description: "Returned to your own account.",
        variant: "success",
      });
    } finally {
      setBusy(false);
    }
  };

  // `data-sticky-top` opts this banner into StickyBar's pinned-banner
  // measurement (src/components/ui/StickyBar.jsx): while it is pinned, any
  // <StickyBar> on the page parks BELOW it instead of under it.
  return (
    <div
      role="status"
      data-sticky-top=""
      className="sticky top-0 z-40 w-full bg-amber-500 text-amber-950 border-b border-amber-700 shadow-md"
    >
      <div className="w-11/12 mx-auto flex items-center justify-between gap-3 py-1.5 text-xs md:text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <UserCog size={16} className="shrink-0" />
          <span className="truncate font-semibold">
            You're acting as {target?.name || "another employee"}
            {target?.position ? ` (${target.position})` : ""} — your real account is still you.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={onQuickLog} disabled={busy} className="bg-amber-100 hover:bg-amber-50 text-amber-950 border border-amber-700">
            <Repeat size={14} className="mr-1" />
            Quick Log
          </Button>
          <Button size="sm" onClick={onSwapBack} disabled={busy} className="bg-amber-950 hover:bg-amber-900 text-amber-50">
            {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <LogOut size={14} className="mr-1" />}
            Swap back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
